import { defineStore } from 'pinia'
import {
  APIS,
  AttendanceWarningColorMap,
  ATTENDEE_TYPES,
  LOADERS,
} from 'src/config/constants'
import { useLoader } from 'src/hooks/useLoader'
import type {
  IAttendance,
  IAttendanceNode,
  IAttendee,
  ITeam,
  IWarning,
} from 'src/interfaces/attendance'
import {
  WARNING_LEVEL_MAP,
  WarningLevel,
  WarningLevelInteger,
} from 'src/interfaces/attendance'
import type { IDraggable, IPoint } from 'src/interfaces/common'
import type { IFamily, IFamilyHasFlaggedMembers } from 'src/interfaces/family'
import type { IGroup } from 'src/interfaces/group'
import type { IPod } from 'src/interfaces/school'
import type { IStaff } from 'src/interfaces/staff'
import { generateAttendanceNode } from 'src/utils/attendance'
import { fail } from 'src/utils/notify'
import type { ISpace } from 'src/interfaces/space'
import { useResStore } from './res.store'
import { useDeviceConfigStore, useUserConfigStore } from 'src/common/store'
import { toObject } from 'src/utils/common'

export interface IAttState {
  attendances: IAttendance[]
  attendancesNodes: IAttendanceNode[]
  attendancesMap: Dictionary<IAttendance>
  attendeesMap: Dictionary<{ space: ISpace; team: ITeam; attendee: IAttendee }>
  studentsMap: Dictionary<ISpace> // Updated: now maps studentId to ISpace
  attendStaffIds: string[]
  attendStudentIds: string[]
  shapeIsDrag: boolean
  dragOffset: IPoint | null
  dragEntity: IDraggable | null
  selectedDragEntity: IDraggable | null
  coordinateStart: IPoint
  attendanceFamilies: IFamily[]
  //new for card view
  staffs: Pinnable<IStaff>[]
  students: IAttendee[]
  flaggedMembers: Dictionary<IFamilyHasFlaggedMembers>
  flaggedMembersIsLoading: boolean
  attendancesIsLoading: boolean
  spaceIsLoading: boolean
  pods?: IPod[]
  podIsLoading: boolean
  groups?: IGroup[]
  groupIsLoading: boolean
  isInitialized: boolean
}

export const useAttStore = defineStore('defina/att', {
  state: (): IAttState => ({
    attendances: [],
    attendancesNodes: [],
    attendancesMap: {},
    attendeesMap: {},
    studentsMap: {}, // Updated: now maps studentId to ISpace
    attendStaffIds: [],
    attendStudentIds: [],
    shapeIsDrag: false,
    dragOffset: null,
    dragEntity: null,
    selectedDragEntity: null,
    coordinateStart: { x: 0, y: 0 },
    attendanceFamilies: [],
    //new for card view
    staffs: [],
    students: [],
    flaggedMembers: {},
    flaggedMembersIsLoading: false,
    spaceIsLoading: false,
    attendancesIsLoading: false,
    podIsLoading: false,
    groupIsLoading: false,
    isInitialized: false,
  }),
  getters: {
    isInitializing: (state) =>
      state.spaceIsLoading ||
      state.attendancesIsLoading ||
      state.flaggedMembersIsLoading ||
      state.podIsLoading ||
      state.groupIsLoading,
    spaceInfos: (state) => {
      const spaces = useResStore().spaces
      const deviceConfigStore = useDeviceConfigStore()
      //state.attendances
      if (!spaces || !Array.isArray(spaces)) {
        return []
      }
      const result = spaces
        .filter((space) => space.id && state.attendancesMap[space.id])
        .map((space) => {
          const spaceId = space.id!
          const attendance = state.attendancesMap[spaceId]

          const teams = attendance.teams
          const attendanceId = attendance.id
          const staff: IAttendee[] = []
          const students: IAttendee[] = []
          const attendanceWarnings = attendance.attendanceWarnings.filter(
            (a) => a.title !== 'Napping',
          )

          teams.map((team) => {
            team.attendees.forEach((attendee) => {
              if (attendee && attendee.type) {
                if (attendee.type === ATTENDEE_TYPES.staff) staff.push(attendee)
                if (attendee.type === ATTENDEE_TYPES.student)
                  students.push(attendee)
              }
            })
          })

          const noOfStudents = students.length
          const isNapping =
            attendance.attendanceWarnings?.some((a) => a.title === 'Napping') ??
            false
          const hasExtraStaff = teams.some((team) =>
            team.teamWarnings?.some((a) => a.title === 'Extra staff'),
          )
          const isEmpty = teams.length < 1
          const isPinned = deviceConfigStore.pinnedSpace.includes(spaceId)

          return {
            id: spaceId,
            color: space.color,
            name: space.name,
            isPinned,
            attendanceId,
            teams,
            noOfTeams: teams.length,
            staff,
            noOfStaff: staff.length,
            students,
            noOfStudents,
            attendanceWarnings,
            isNapping,
            hasExtraStaff,
            isEmpty,
            original: space,
            warningColor: warningColor(isEmpty, teams, attendanceWarnings),
          }
        })
        .sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1 // a (pinned) comes before b (not pinned)
          if (!a.isPinned && b.isPinned) return 1 // b (pinned) comes before a (not pinned)
          return 0 // Keep the original order for non-pinned items
        })

      return result
    },
  },
  actions: {
    setShapeIsDrag(isDragging: boolean) {
      this.shapeIsDrag = isDragging
    },
    setDragOffset(dragOffset: IPoint | null) {
      if (!this.dragOffset) {
        this.dragOffset = dragOffset
        return
      }
      if (!dragOffset) {
        this.dragOffset = null
        return
      }
      this.dragOffset.x += Math.floor(dragOffset.x / 10) * 10
      this.dragOffset.y += Math.floor(dragOffset.y / 10) * 10
    },
    replaceAttendanceNode(attendance: IAttendanceNode) {
      const index = this.attendancesNodes.findIndex(
        (n) => n.id === attendance.id,
      )
      if (index === -1) return
      this.attendancesNodes[index] = attendance
    },
    setDragEntity(dragEntity: IDraggable | null) {
      this.dragEntity = dragEntity
    },
    setSelectedDragEntity(dragEntity: IDraggable | null) {
      this.selectedDragEntity = dragEntity
    },
    addAnchor(index: number) {
      if (this.selectedDragEntity && this.selectedDragEntity.shape) {
        this.selectedDragEntity.shape.points.splice(index + 1, 0, {
          x: this.selectedDragEntity.shape.points[index].x + 20,
          y: this.selectedDragEntity.shape.points[index].y + 20,
        })
      }
    },
    removeAnchor(index: number) {
      if (this.selectedDragEntity && this.selectedDragEntity.shape) {
        if (this.selectedDragEntity.shape.points.length > 3)
          this.selectedDragEntity.shape.points.splice(index, 1)
      }
    },
    removeShapeFromSpace() {
      if (this.selectedDragEntity) this.selectedDragEntity.shape = null
    },
    setCoordinateStart(point: IPoint) {
      const newCoordinate = {
        x: Math.floor(point.x / 10) * 10,
        y: Math.floor(point.y / 10) * 10,
      }
      this.coordinateStart = newCoordinate
    },
    setAttendanceFamilies(families: IFamily[]) {
      this.attendanceFamilies = families
    },
    //Actions
    async getAttendanceFamilies() {
      const {
        useFetchLoader: fetchFamilies,
        result: families,
        error: fetchFamiliesError,
      } = useLoader<IFamily[]>(LOADERS.per, { uri: APIS.families })
      await fetchFamilies()
      if (fetchFamiliesError && fetchFamiliesError.value) {
        fail(fetchFamiliesError.value.message)
        return
      }
      this.setAttendanceFamilies(families.value ?? [])
    },
    setAttendancesAndNodes(attendances: IAttendance[]) {
      this.attendances = attendances
      const {
        attendanceNodes,
        attendStaffIds,
        attendStudentIds,
        attendancesMap,
        attendeesMap,
        studentsMap, // Add studentsMap from transform function
      } = transformToSignedInList(attendances)
      this.attendancesNodes = attendanceNodes
      this.attendancesMap = attendancesMap
      this.attendStaffIds = attendStaffIds
      this.attendStudentIds = attendStudentIds
      this.attendeesMap = attendeesMap
      this.studentsMap = studentsMap // Set the studentsMap
    },
    //new for card view
    async pinStaff(staffId: string) {
      const staffFound = this.staffs.find((s) => s.id === staffId)
      if (!staffFound) return

      staffFound.isPinned = !staffFound.isPinned
      const pinnedStaffIds = this.staffs
        .filter((staff) => staff.isPinned)
        .map((staff) => staff.id)
      const userConfigStore = useUserConfigStore()
      await userConfigStore.set('pinnedStaff', pinnedStaffIds)
    },
    async pinSpace(spaceId: string) {
      const deviceConfigStore = useDeviceConfigStore()
      const pinnedSpaceIds = [...deviceConfigStore.pinnedSpace]

      const index = pinnedSpaceIds.indexOf(spaceId)
      if (index > -1) pinnedSpaceIds.splice(index, 1)
      else pinnedSpaceIds.push(spaceId)

      deviceConfigStore.set('pinnedSpace', pinnedSpaceIds)
    },
    async loadAttendancesAsync() {
      this.attendancesIsLoading = true
      try {
        const {
          useFetchLoader: fetchAttendances,
          error: fetchAttendancesError,
        } = useLoader(LOADERS.attendance, { uri: APIS.attendance })
        await fetchAttendances().then((result) => {
          this.setAttendancesAndNodes(result ?? [])
        })
        if (fetchAttendancesError.value) {
          fail(fetchAttendancesError.value.message)
        }
      } finally {
        this.attendancesIsLoading = false
      }
    },
    async loadSpacesAsync() {
      const { useFetchLoader: fetchSpaces, error: fetchSpacesError } =
        useLoader(LOADERS.res, {
          uri: APIS.spaces,
        })
      this.spaceIsLoading = true
      try {
        await fetchSpaces().then((result) => useResStore().setSpaces(result))
        if (fetchSpacesError.value) {
          fail(fetchSpacesError.value.message)
        }
      } finally {
        this.spaceIsLoading = false
      }
    },
    isFlaggedMember(familyId: string) {
      return this.flaggedMembers[familyId]
    },
    async loadStudentsAsync() {
      const { useFetchLoader: fetchAttendees } = useLoader<IAttendee[]>(
        LOADERS.per,
        {
          uri: APIS.attendees,
        },
      )
      await fetchAttendees().then((result) => (this.students = result ?? []))

      // Load additional medical data for students
      await this.loadStudentMedicalDataAsync()
    },

    async loadStudentMedicalDataAsync() {
      // Load complete student data with medical information
      const { useFetchLoader: fetchStudents } = useLoader<any[]>(LOADERS.per, {
        uri: APIS.students,
      })
      await fetchStudents().then((result) => {
        const fullStudents = result ?? []

        // Enrich existing students with medical data
        this.students = this.students.map((student: any) => {
          const fullStudent = fullStudents.find(
            (fs: any) => fs.id === student.id,
          )
          if (fullStudent) {
            return {
              ...student,
              hasMedications:
                fullStudent.medications && fullStudent.medications.length > 0,
              hasAllergies:
                fullStudent.medicalConditions &&
                fullStudent.medicalConditions.some((condition: any) =>
                  condition.name.toLowerCase().includes('allerg'),
                ),
              hasSpecialNeeds:
                fullStudent.specialNeeds && fullStudent.specialNeeds.length > 0,
              medicalInfo: {
                medications: fullStudent.medications || [],
                medicalConditions: fullStudent.medicalConditions || [],
                specialNeeds: fullStudent.specialNeeds || [],
              },
            }
          }
          return student
        })
      })
    },
    async loadStaffsAsync() {
      const { useFetchLoader: fetchStaff } = useLoader<IStaff[]>(LOADERS.per, {
        uri: APIS.staff,
      })
      await fetchStaff().then((result) => {
        const pinnedStaffIds = useUserConfigStore().pinnedStaff
        if (!result) {
          this.staffs = []
          return
        }
        this.staffs = result.map((staff) => ({
          ...staff,
          isPinned: staff.id ? pinnedStaffIds.includes(staff.id) : false,
        }))
      })
    },
    async loadFlaggedMembersAsync() {
      const { useFetchLoader: fetchFlaggedMembers } = useLoader<
        IFamilyHasFlaggedMembers[]
      >(LOADERS.per, {
        uri: `${APIS.families}/${APIS.flaggedMembers}`,
      })
      this.flaggedMembersIsLoading = true
      try {
        const response = await fetchFlaggedMembers()
        if (!response) {
          this.flaggedMembers = {}
          return
        }

        this.flaggedMembers = toObject(response, (obj) => obj.id)
      } finally {
        this.flaggedMembersIsLoading = false
      }
    },
    async loadPodsAsync() {
      this.podIsLoading = true
      try {
        const { useFetchLoader: fetchPod } = useLoader<IPod[]>(LOADERS.res, {
          uri: APIS.pods,
        })

        this.pods = await fetchPod()
      } finally {
        this.podIsLoading = false
      }
    },

    async loadGroupsAsync() {
      this.groupIsLoading = true
      try {
        const { useFetchLoader: fetchGroup } = useLoader<IGroup[]>(
          LOADERS.res,
          {
            uri: APIS.groups,
          },
        )
        this.groups = await fetchGroup()
      } finally {
        this.groupIsLoading = false
      }
    },

    async InitAsync() {
      this.isInitialized = false
      await Promise.all([
        this.loadAttendancesAsync(),
        this.loadStudentsAsync(),
        this.loadStaffsAsync(),
        this.loadFlaggedMembersAsync(),
        this.loadPodsAsync(),
        this.loadGroupsAsync(),
        this.loadSpacesAsync(),
      ])
      this.isInitialized = true
    },
  },
})

const transformToSignedInList = (data: IAttendance[]) => {
  const attendStaffIds: string[] = []
  const attendStudentIds: string[] = []
  const attendancesMap: Dictionary<IAttendance> = {}
  const attendeesMap: Dictionary<{
    space: ISpace
    team: ITeam
    attendee: IAttendee
  }> = {}
  const studentsMap: Dictionary<ISpace> = {} // New: maps studentId to ISpace

  const attendanceNodes = (data || []).reduce(
    (acc: IAttendanceNode[], att: IAttendance) => {
      if (att.space && att.space.id) {
        //need to aggregate data
        attendancesMap[att.space.id] = att
      }

      att.teams.forEach((team) => {
        team.attendees.forEach((attendee) => {
          attendeesMap[attendee.id!] = {
            space: att.space,
            team,
            attendee,
          }

          // New logic: Map students to their assigned space
          if (
            attendee.type === ATTENDEE_TYPES.student &&
            attendee.id &&
            att.space
          ) {
            studentsMap[attendee.id] = att.space
          }
        })
      })

      const {
        attendanceNode,
        attendStaffIds: localStaffIds,
        attendStudentIds: localStudentIds,
      } = generateAttendanceNode(att)
      acc.push(attendanceNode)
      attendStudentIds.push(...localStudentIds)
      attendStaffIds.push(...localStaffIds)
      return acc
    },
    [],
  )

  return {
    attendanceNodes,
    attendStaffIds,
    attendStudentIds,
    attendancesMap,
    attendeesMap,
    studentsMap, // Return the new studentsMap
  }
}

const warningColor = (
  isEmpty: boolean,
  teams: ITeam[],
  warnings: IWarning[],
) => {
  const getWarningLevel = (warnings: IWarning[]) => {
    if (
      warnings.some(
        (w) =>
          AttendanceWarningColorMap(true)[w.title] === WarningLevel.Negative,
      )
    ) {
      return WarningLevelInteger.Negative
    }

    if (
      warnings.some(
        (w) =>
          AttendanceWarningColorMap(true)[w.title] === WarningLevel.Warning,
      )
    ) {
      return WarningLevelInteger.Warning
    }

    return WarningLevelInteger.Positive
  }

  if (isEmpty) {
    return
  }

  let teamWarningLevel = -1
  if (teams) {
    const allTeamWarnings: IWarning[] = []
    teams.forEach((t) => {
      allTeamWarnings.push(...t.teamWarnings)
    })
    teamWarningLevel = getWarningLevel(allTeamWarnings)
  }
  let attendanceWarningLevel = -1

  if (warnings.length > 0) {
    attendanceWarningLevel = getWarningLevel(warnings)
  }

  if (teamWarningLevel > attendanceWarningLevel) {
    return WARNING_LEVEL_MAP.get(teamWarningLevel)
  }
  return WARNING_LEVEL_MAP.get(attendanceWarningLevel)
}
